import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowLeft } from "lucide-react";

import { ToolbarSticky } from "@/components/wyvio-ui/signature/ToolbarSticky";
import {
  DeliveryRow,
  DeliverySection,
} from "@/components/wyvio-ui/signature/DeliveryRow";

/**
 * PlanningMockup V2 — Journée cockpit WYVIO.
 * Mission unique : "Qu'est-ce que mon équipe doit préparer aujourd'hui ?"
 *
 * Verrouillages :
 *   • L'heure est l'élément dominant de chaque ligne (pas la photo).
 *   • Cartes compactes : 10 livraisons visibles sans scroll excessif.
 *   • Miniature véhicule ≈ 15 % de la largeur d'une ligne.
 *   • Tri naturel : Prochaine (mise en avant) → En cours → À venir → Terminées (repliées).
 *   • Résumé opérationnel en tête + carte "Prochaine".
 *   • Livraisons terminées presque invisibles.
 *
 * Test "sans texte" : silhouettes véhicules, heures XL tabulaires, workflow bar 13,
 * badges pilule = univers automobile immédiatement reconnaissable.
 */

const DAYS = [
  { key: "today", label: "Aujourd'hui", date: "Mardi 24 février", total: 8 },
  { key: "tomorrow", label: "Demain", date: "Mercredi 25 février", total: 5, note: "2 créneaux en attente client" },
  { key: "d2", label: "Vendredi", date: "Vendredi 27 février", total: 3 },
];

const DELIVERIES = [
  {
    id: 1,
    time: "08h30",
    vehicle: "BMW M4 Competition",
    client: "Sébastien Rossi",
    commercial: "Kevin",
    location: "Paris 8e",
    silhouette: "coupe",
    state: "terminee",
  },
  {
    id: 2,
    time: "09h45",
    vehicle: "Range Rover Autobiography",
    client: "Alice Beaumont",
    commercial: "Stéphane",
    location: "Saint-Cloud",
    silhouette: "suv",
    state: "terminee",
  },
  {
    id: 3,
    time: "10h30",
    vehicle: "Porsche 911 GT3",
    client: "Marc Ballaud",
    commercial: "Stéphane",
    location: "Paris 8e",
    silhouette: "coupe",
    state: "imminente",
    countdown: "Dans 18 min",
    checklist: [
      { label: "Paiement", done: true },
      { label: "CPI", done: true },
      { label: "Préparation", done: true },
      { label: "Signature", done: false },
    ],
  },
  {
    id: 4,
    time: "11h15",
    vehicle: "Audi RS6 Performance",
    client: "Louis Marchand",
    commercial: "Kevin",
    location: "Paris 16e",
    silhouette: "break",
    state: "encours",
    checklist: [
      { label: "Paiement", done: true },
      { label: "CPI", done: true },
    ],
  },
  {
    id: 5,
    time: "13h00",
    vehicle: "Mercedes-Benz Classe G",
    client: "Familia Dupont",
    commercial: "Amina",
    location: "Neuilly",
    silhouette: "suv",
    state: "encours",
    checklist: [
      { label: "Paiement", done: true },
      { label: "Préparation", done: false },
    ],
  },
  {
    id: 6,
    time: "14h45",
    vehicle: "Tesla Model S Plaid",
    client: "Antoine Vidal",
    commercial: "Stéphane",
    location: "Lyon",
    silhouette: "berline",
    state: "avenir",
    checklist: [
      { label: "Paiement", done: true },
      { label: "CPI", done: true },
      { label: "Préparation", done: true },
    ],
  },
  {
    id: 7,
    time: "16h45",
    vehicle: "Porsche Taycan Turbo",
    client: "Groupe Élégance",
    commercial: "Kevin",
    location: "Neuilly",
    silhouette: "berline",
    state: "avenir",
    checklist: [
      { label: "Paiement", done: false },
      { label: "CPI", done: true },
      { label: "Préparation", done: true },
    ],
  },
  {
    id: 8,
    time: "18h20",
    vehicle: "Rivian R1T Launch",
    client: "Studios Rivian",
    commercial: "Amina",
    location: "Marseille",
    silhouette: "break",
    state: "avenir",
    checklist: [
      { label: "Paiement", done: true },
      { label: "CPI", done: false },
    ],
  },
];

export default function PlanningMockup() {
  const [activeDay, setActiveDay] = useState("today");
  const day = DAYS.find((d) => d.key === activeDay) || DAYS[0];

  const imminente = DELIVERIES.find((d) => d.state === "imminente");
  const done = DELIVERIES.filter((d) => d.state === "terminee");
  const live = DELIVERIES.filter((d) => d.state === "encours");
  const upcoming = DELIVERIES.filter(
    (d) => d.state === "avenir" && (!imminente || d.id !== imminente.id),
  );
  const total = DELIVERIES.length;
  const remaining = total - done.length;

  return (
    <>
      <Helmet>
        <title>{`Pilotage du jour — ${day.date} · wyvio`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="wyvio-signature-bg font-neue-haas min-h-screen">
        <ToolbarSticky />

        <main className="relative z-10 mx-auto max-w-[1240px] px-12 pb-24 pt-14">
          {/* Retour + tag */}
          <div className="mb-8 flex items-center justify-between">
            <Link
              to="/mockups/dashboard-typo-neue-haas"
              className="group inline-flex items-center gap-2 text-[12.5px] font-medium text-[#6B6B67] transition-colors hover:text-[#151515]"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft
                className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5"
                strokeWidth={2}
              />
              Retour au centre de pilotage
            </Link>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E8E8E2] bg-white/70 px-3 py-1.5 text-[10.5px] font-medium uppercase tracking-[0.2em] text-[#6B6B67] backdrop-blur">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#151515]" />
              Mockup · Planning
            </div>
          </div>

          {/* Résumé opérationnel */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mb-14"
          >
            <p className="text-[10.5px] font-medium uppercase tracking-[0.3em] text-[#8A8A80]">
              Pilotage du jour · {day.date}
            </p>
            <h1 className="mt-6 text-[60px] font-medium leading-[1] tracking-[-0.032em] text-[#151515]">
              {total} livraisons aujourd&apos;hui.
            </h1>

            <div className="mt-10 flex flex-wrap items-center gap-x-14 gap-y-6 border-t border-[#E8E8E2] pt-8">
              <Stat value={done.length} label="Terminées" muted />
              <Stat value={live.length} label="En cours" accent />
              <Stat value={remaining} label="Restantes" />

              {/* Prochaine — ligne éditoriale simple, pas de carte massive */}
              {imminente && (
                <div
                  className="ml-auto flex items-center gap-4 rounded-[16px] bg-white px-6 py-4"
                  style={{
                    boxShadow:
                      "0 1px 2px rgba(21,21,21,0.05), 0 12px 32px -16px rgba(200,255,47,0.4), inset 0 0 0 1px rgba(200,255,47,0.35)",
                  }}
                  data-testid="next-up"
                >
                  <span
                    aria-hidden
                    className="wyvio-segment-active inline-block h-1.5 w-1.5 rounded-full"
                    style={{ background: "#C8FF2F" }}
                  />
                  <div>
                    <p className="text-[10.5px] font-medium uppercase tracking-[0.22em] text-[#8A8A80]">
                      Prochaine · {imminente.countdown}
                    </p>
                    <p className="mt-1.5 text-[15px] font-medium tracking-[-0.01em] text-[#151515]">
                      <span className="wyvio-tabular">{imminente.time}</span>{" "}
                      · {imminente.vehicle} ·{" "}
                      <span className="text-[#6B6B67]">{imminente.client}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Switcher jours discret */}
            <div className="mt-10 flex items-center gap-3">
              {DAYS.map((d) => (
                <button
                  key={d.key}
                  onClick={() => setActiveDay(d.key)}
                  data-testid={`day-${d.key}`}
                  className={`rounded-full px-4 py-2 text-[12.5px] font-medium tracking-[-0.005em] transition-all ${
                    d.key === activeDay
                      ? "bg-[#151515] text-white"
                      : "bg-transparent text-[#6B6B67] hover:bg-[#F2F2EE]"
                  }`}
                >
                  {d.label}
                  <span
                    className={`ml-2 inline-block rounded-full px-1.5 py-0.5 wyvio-tabular text-[10px] font-medium ${
                      d.key === activeDay
                        ? "bg-white/15 text-white"
                        : "bg-[#F2F2EE] text-[#8A8A80]"
                    }`}
                  >
                    {d.total}
                  </span>
                </button>
              ))}
            </div>
          </motion.section>

          {/* Sections triées naturellement : Prochaine → En cours → À venir → Livrées */}
          {imminente && (
            <DeliverySection
              title="Prochaine"
              count={1}
              testid="section-next"
            >
              <DeliveryRow delivery={imminente} testid={`row-${imminente.id}`} />
            </DeliverySection>
          )}

          {live.length > 0 && (
            <DeliverySection title="En cours" count={live.length} testid="section-live">
              {live.map((d, i) => (
                <DeliveryRow
                  key={d.id}
                  delivery={d}
                  index={i}
                  testid={`row-${d.id}`}
                />
              ))}
            </DeliverySection>
          )}

          {upcoming.length > 0 && (
            <DeliverySection
              title="À venir"
              count={upcoming.length}
              testid="section-upcoming"
            >
              {upcoming.map((d, i) => (
                <DeliveryRow
                  key={d.id}
                  delivery={d}
                  index={i}
                  testid={`row-${d.id}`}
                />
              ))}
            </DeliverySection>
          )}

          {done.length > 0 && (
            <DeliverySection
              title="Livrées"
              count={done.length}
              collapsible
              defaultCollapsed
              testid="section-done"
            >
              {done.map((d, i) => (
                <DeliveryRow
                  key={d.id}
                  delivery={d}
                  index={i}
                  testid={`row-${d.id}`}
                />
              ))}
            </DeliverySection>
          )}
        </main>
      </div>
    </>
  );
}

/**
 * Stat — micro-composant pour l'agrégat opérationnel.
 * Chiffre XL tabulaire + micro-label uppercase.
 */
function Stat({ value, label, accent = false, muted = false }) {
  return (
    <div>
      <div className="flex items-baseline gap-2">
        {accent && (
          <span
            aria-hidden
            className="wyvio-segment-active mr-1 inline-block h-1.5 w-1.5 rounded-full"
            style={{ background: "#C8FF2F" }}
          />
        )}
        <span
          className={`wyvio-tabular text-[44px] font-medium leading-none tracking-[-0.03em] ${
            muted ? "text-[#B8B8B0]" : "text-[#151515]"
          }`}
        >
          {value}
        </span>
      </div>
      <p className="mt-3 text-[10.5px] font-medium uppercase tracking-[0.22em] text-[#8A8A80]">
        {label}
      </p>
    </div>
  );
}
